import React, { useCallback, useState, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'
import ReleaseNoteLayout, { ReleaseNoteMain, ReleaseNoteSidebar } from './ReleaseNoteLayout'

import PageLoader from 'Layout/PageLoader'

import Tags from '../../../../components/Tags'

import ReleaseNoteForm from './ReleaseNoteForm'
import releaseNoteFormValidationSchema from './ReleaseNoteForm/release-note-form-validation-schema'
import {
  useGetReleaseNoteByIdQuery,
  useUpdateReleaseNoteMutation,
  useDeleteReleaseNoteMutation
} from './release-notes.service'
import parseErrorMessage from 'Utils/error-message-parser'
import SwitchInput from 'Design/components/SwitchInput'
import ConfirmationModal from 'Design/components/ConfirmationModal'
import { CATEGORIES } from './constants'
let categoriesOptions

const generateCategoryOptions = () => {
  if (!categoriesOptions) {
    categoriesOptions = []
    for (const key in CATEGORIES) {
      const label = CATEGORIES[key]
      const value = key
      categoriesOptions.push({
        label,
        value
      })
    }
  }
  return categoriesOptions
}

const CategoriesInput = ({ isUpdating, field: { value, onChange } }) => {
  const handleTagRemove = (tag) => {
    const newTags = value.filter((t) => t.value !== tag.value)
    onChange(newTags)
  }

  return (
    <Tags
      placeholder='Add categories'
      value={value}
      onChange={onChange}
      onRemove={handleTagRemove}
      options={generateCategoryOptions()}
      isDisabled={isUpdating}
      isLoading={isUpdating}
    />
  )
}

const ReleaseNoteFormHandler = (props) => {
  const { releaseNote } = props
  const navigate = useNavigate()

  const categoriesValue = useMemo(() => {
    return releaseNote.categories?.map?.((category) => {
      return {
        label: CATEGORIES[category],
        value: category
      }
    })
  }, [releaseNote.categories])

  const formMethods = useForm({
    defaultValues: {
      title: releaseNote.title,
      body: releaseNote.body,
      is_visible: releaseNote.is_visible,
      product: {
        value: releaseNote.product_id
      },
      categories: categoriesValue,
      release_date: releaseNote.release_date
    },
    resolver: yupResolver(releaseNoteFormValidationSchema)
  })

  const [updateReleaseNote, releaseNoteResult] = useUpdateReleaseNoteMutation()
  const [deleteReleaseNote, deleteReleaseNoteResult] = useDeleteReleaseNoteMutation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const update = useCallback(
    (payload) => {
      const promise = updateReleaseNote({ id: releaseNote.id, ...payload }).unwrap()
      toast.promise(promise, {
        loading: 'Updating release note...',
        success: 'Release note updated successfully.',
        error: parseErrorMessage('Unable to update release note. Try again...')
      })
      return promise
    },
    [updateReleaseNote, releaseNote.id]
  )

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        id: releaseNote.id,
        title: data.title,
        body: data.body,
        is_visible: data.is_visible,
        categories: data.categories.map((category) => category.value),
        product_id: data.product?.value,
        release_date: data.release_date
      }

      update(payload)
    },
    [releaseNote.id, update]
  )

  const onDeleteReleaseNote = useCallback(() => {
    const promise = deleteReleaseNote(releaseNote.id).unwrap()

    toast.promise(promise, {
      loading: 'Deleting release note...',
      success: 'Release note deleted successfully.',
      error: parseErrorMessage('Unable to delete release note. Try again...')
    })

    promise.then(() => {
      navigate('../')
    })
  }, [releaseNote.id, deleteReleaseNote, navigate])

  const onDelete = () => {
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
  }

  const { isLoading } = releaseNoteResult.isLoading || deleteReleaseNoteResult.isLoading
  const isUpdating = releaseNoteResult.isLoading

  return (
    <ReleaseNoteLayout>
      <ConfirmationModal
        isOpen={showDeleteModal}
        title='Delete Release Note'
        description='Are you sure to delete this release note?'
        onCloseButtonClick={handleCloseDeleteModal}
        onConfirmButtonClick={onDeleteReleaseNote}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isLoading}
      />
      <ReleaseNoteMain>
        <FormProvider {...formMethods}>
          <ReleaseNoteForm mode='edit' onSubmit={onSubmit} onDelete={onDelete} isLoading={isLoading} />
        </FormProvider>
      </ReleaseNoteMain>
      <ReleaseNoteSidebar>
        <Controller
          control={formMethods.control}
          name='is_visible'
          render={({ field: { value, onChange } }) => {
            return (
              <SwitchInput checked={value} onChange={onChange}>
                Visible
              </SwitchInput>
            )
          }}
        />
        <Controller control={formMethods.control} name='categories' isUpdating={isUpdating} render={CategoriesInput} />
      </ReleaseNoteSidebar>
    </ReleaseNoteLayout>
  )
}

const EditReleaseNote = () => {
  const { id } = useParams()
  const { isLoading, data: releaseNote } = useGetReleaseNoteByIdQuery(id)

  if (isLoading) {
    return <PageLoader />
  }

  return <ReleaseNoteFormHandler releaseNote={releaseNote} />
}

export default EditReleaseNote
